import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { GET_EXPIRED_LISTING } from "@/graphql/queries/user";
import { useAuthState } from "@/contexts/auth/auth.provider";
import config from "@/common/config";
import logger from "@/console";

const useExpiredListings = ({
  pageSize = config.LISTINGS_PAGE_SIZE,
  autoFetch = true,
  enabled = true,
} = {}) => {
  const { token } = useAuthState();
  const [allListings, setAllListings] = useState([]);
  const [totalListings, setTotalListings] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [error, setError] = useState(null);
  const [hasCompleted, setHasCompleted] = useState(false);

  const { loading, data, fetchMore } = useQuery(GET_EXPIRED_LISTING, {
    skip: !enabled || !token,
    context: { token },
    variables: {
      from: 0,
      size: pageSize,
    },
    fetchPolicy: "no-cache",
    onCompleted: ({ User }) => {
      const listings = User?.expiredListings?.listings || [];
      const total = User?.expiredListings?.total || 0;

      setAllListings(listings);
      setTotalListings(total);
      setCurrentOffset(listings.length);

      if (autoFetch && listings.length < total && !hasCompleted) {
        setIsFetchingAll(true);
      } else if (listings.length >= total) {
        setHasCompleted(true);
      }
    },
    onError: (err) => {
      setError(err);
      logger.error("Error fetching expired listings:", err);
    },
  });

  const fetchMoreListings = useCallback(async () => {
    if (!fetchMore || currentOffset >= totalListings || hasCompleted) {
      return;
    }

    try {
      const { data: fetchMoreData } = await fetchMore({
        variables: {
          from: currentOffset,
          size: pageSize,
        },
      });

      const newListings = fetchMoreData?.User?.expiredListings?.listings || [];
      const newTotal =
        fetchMoreData?.User?.expiredListings?.total || totalListings;

      setAllListings((prev) => [...prev, ...newListings]);
      setTotalListings(newTotal);
      setCurrentOffset((prev) => prev + newListings.length);

      if (currentOffset + newListings.length >= newTotal) {
        setHasCompleted(true);
        setIsFetchingAll(false);
      }
    } catch (err) {
      setError(err);
      setIsFetchingAll(false);
      logger.error("Error fetching more expired listings:", err);
    }
  }, [fetchMore, currentOffset, totalListings, hasCompleted, pageSize]);

  useEffect(() => {
    if (
      autoFetch &&
      !hasCompleted &&
      totalListings > 0 &&
      currentOffset < totalListings
    ) {
      setIsFetchingAll(true);
    }
  }, [autoFetch, hasCompleted, totalListings, currentOffset]);

  useEffect(() => {
    if (isFetchingAll && !hasCompleted && currentOffset < totalListings) {
      fetchMoreListings();
    }
  }, [
    isFetchingAll,
    hasCompleted,
    currentOffset,
    totalListings,
    fetchMoreListings,
  ]);

  const fetchAllRemaining = useCallback(() => {
    if (!hasCompleted && currentOffset < totalListings) {
      setIsFetchingAll(true);
    }
  }, [hasCompleted, currentOffset, totalListings]);

  const reset = useCallback(() => {
    setAllListings([]);
    setTotalListings(0);
    setCurrentOffset(0);
    setIsFetchingAll(false);
    setError(null);
    setHasCompleted(false);
  }, []);

  return {
    listings: allListings,
    total: totalListings,

    loading: loading || isFetchingAll,
    initialLoading: loading,
    fetchingMore: isFetchingAll,

    hasCompleted,
    hasMore: currentOffset < totalListings,

    error,

    fetchMore: fetchMoreListings,
    fetchAllRemaining,
    reset,
  };
};

export default useExpiredListings;
